import { returnAllTradeDetailsFromGenericParams } from "@/lib/agGridColDefs/colDefGlobalHelpers";
import tradeDataStore from "@/store/tradeDataStore";
import { useTheme } from "next-themes";
import clsx from "clsx";
import { formatFillPriceWithNotation } from "./fillPriceFormatter";
import {
  getFillPriceFromStore,
  getFillPriceCellStyling,
  getNotationFromStore,
} from "./fillPriceHelpers";
import { resolveNotationFromParams } from "@/lib/currencySymbolHelper";
import {
  containsHyphenInQuote,
  formatDollarFractionString,
  formatDollarFractionZValueForSmallFractionDisplay,
  returnFractionString,
  ruleOfEights,
} from "@/lib/businessRules/dollarFractionBusinessRule";
import { ReactNode, useEffect, useState } from "react";
import { getFlashStyles, FlashKeyframes } from "@/lib/styles/flashStyles";
import {
  isFillCancelled,
  isFillLocked,
  getFillFromStore,
} from "../../fillStateHelpers";

/**
 * FillPriceCellRenderer
 *
 * Displays the fill price value from the fills array with real-time Zustand updates.
 * For RP trades, applies bid/ask/mid styling (rounded input style).
 * For OP trades, displays value without special styling.
 *
 * Handles special states:
 * - Cancelled fills: Display historic price with strikethrough
 * - Locked fills (timestamp): Display current price with disabled styling
 *
 * Works in conjunction with fillPriceValueFormatter for copy/paste support.
 */
const FillPriceCellRenderer = (params: any) => {
  const { theme } = useTheme();
  const [isFlashing, setIsFlashing] = useState(false);

  // Subscribe to fillsToFlash for validation highlighting
  const fillsToFlash = tradeDataStore((state) => state.fillsToFlash);

  // Subscribe to tradeData for reactive updates on fill state changes
  const tradeData = tradeDataStore((state) => state.tradeData);

  // Extract data from params (may be undefined if params.data is null)
  const { fullTradeData } = params.data
    ? returnAllTradeDetailsFromGenericParams(params)
    : { fullTradeData: null };
  const esid = fullTradeData?.esid;
  const isRP = fullTradeData?.role === "RP";

  // Get fill details from params.data
  const fillId = params.data?.fillId;
  const blockId = params.data?.blockId;
  const extFillId = params.data?.fillData?.extFillId;

  // Get the full fill object from the store to check its state
  // extFillId fallback: when fillId is stale after backend assigns new fillId
  const fill =
    esid && fillId !== undefined && blockId !== undefined
      ? getFillFromStore(tradeData, esid, blockId, fillId, extFillId)
      : null;

  const isCancelled = fill ? isFillCancelled(fill) : false;
  const isEditableFillType =
    fill?.fillType === undefined ||
    fill?.fillType === "New" ||
    fill?.fillType === "Updated";
  const isLocked = fill ? isFillLocked(fill) : false;

  // Check if this fill should be flashing (validation error)
  // Use loose equality (==) for fillId and blockId to handle number/string type mismatches
  const shouldFlash = fillsToFlash.some(
    (f) => f.esid === esid && f.blockId == blockId && f.fillId == fillId,
  );

  // Handle flash effect - sync local state with store state
  useEffect(() => {
    if (shouldFlash) {
      setIsFlashing(true);
    } else {
      // When store clears, stop flashing
      setIsFlashing(false);
    }
  }, [shouldFlash]);

  if (!params.data) {
    return null;
  }

  // Determine which price to display
  let displayPrice: string | undefined = undefined;

  if (isCancelled && fill?.historicFillRate !== undefined) {
    // Cancelled fills: show the historic price
    displayPrice = fill.historicFillRate;
  } else if (esid && fillId !== undefined && blockId !== undefined) {
    // Normal fills: get fresh value from store
    displayPrice = getFillPriceFromStore(esid, fillId, blockId);
  }

  // Fallback to params.value if store doesn't have it (e.g. historical trades)
  if (displayPrice == null) {
    displayPrice = params.value ?? undefined;
  }

  // Get notation for the block and resolve "$" to actual currency symbol
  const notation =
    esid && blockId !== undefined
      ? getNotationFromStore(esid, blockId)
      : undefined;
  const displayNotation = resolveNotationFromParams(notation, params);

  // Handle $fr notation: convert Z-value digit to superscript fraction (e.g., "99-163" → "99-16 ³⁄₈ $fr")
  let formattedValue: ReactNode = "";
  if (
    displayNotation === "$fr" &&
    displayPrice &&
    typeof displayPrice === "string" &&
    containsHyphenInQuote(displayPrice)
  ) {
    const fractionStr = formatDollarFractionString(displayPrice);
    const valueAfterHyphen = returnFractionString(displayPrice, false);

    if (valueAfterHyphen.length === 3) {
      const quoteDisplayValue = fractionStr.slice(0, -1);
      const zValueDecimal = formatDollarFractionZValueForSmallFractionDisplay(
        displayPrice,
        ruleOfEights,
      );
      formattedValue = <>{quoteDisplayValue}{zValueDecimal}</>;
    } else {
      formattedValue = fractionStr;
    }
  } else {
    formattedValue = formatFillPriceWithNotation(displayPrice, displayNotation);
  }
  const cellStyling = getFillPriceCellStyling(isRP, theme);
  const flashStyles = getFlashStyles(isFlashing);

  return (
    <>
      <FlashKeyframes isFlashing={isFlashing} />
      <div
        data-testid="fillPriceCellContainer"
        className={clsx(
          "justify-center text-center",
          isRP &&
            !isLocked &&
            !isFlashing &&
            !isCancelled &&
            isEditableFillType &&
            `ag-input ag-roundedInputStyle border ${cellStyling}`,
          isLocked && "opacity-60",
        )}
        style={{
          ...flashStyles,
          ...(isCancelled && {
            textDecoration: "line-through",
            opacity: 0.5,
            color: "#888",
          }),
        }}
      >
        {formattedValue}
      </div>
    </>
  );
};

export default FillPriceCellRenderer;
